import { ldb, state } from '@codesuma/baseline'
import { fallback, flushAll, flushPending, pendingCollections, rememberPut, rememberRemove } from './fallback'
import { idb, ensureStores } from './idb'
import { runImport } from './import'
import { local } from './local'
import { openSqlite, sqlite } from './sqlite'

// Collection reads fill the synchronous in-memory stores during boot. The
// stores fire-and-forget their writes, so the facade chains put/remove per
// collection and mutations reach storage in call order. load runs at boot
// before any write exists and needs no chain.

export interface ICollection<T extends { id: string }> {
    // Boot fill; later stages add a ranged read alongside it.
    load(): Promise<T[]>
    put(row: T): Promise<void>
    remove(id: string): Promise<void>
}

export type Factory = <T extends { id: string }>(name: string) => ICollection<T>
type EngineName = 'local' | 'sqlite' | 'idb'

// collection() is called at module load, before any engine can be chosen, so
// each call returns a facade that reads this at call time.
let engine: Factory = local
// Whether this session gave up on the real database. It changes what a write
// may do: the fallback engine's own store is the pending delta, so flushing
// the delta into it would hand it back its own rows and then delete them.
let fell = false

const TOGGLE = 'storage-engine'

// Raised while the store cannot be trusted — this session fell back, or a
// write failed and has not gone through since. Lowered as soon as nothing is
// owed. components/storage-notice is what the user sees of it.
const DEGRADED = 'storage-degraded'

function chooseEngine(): EngineName {
    const toggle = ldb.get(TOGGLE)
    if (toggle === 'sqlite' || toggle === 'idb' || toggle === 'local') return toggle
    const native = !!(window as any).Capacitor?.isNativePlatform?.()
    return native ? 'sqlite' : 'idb'
}

export async function openStorage() {
    const want = chooseEngine()
    if (want === 'local') return
    try {
        if (want === 'sqlite') {
            await openSqlite()
            engine = sqlite
        } else {
            await ensureStores()
            engine = idb
        }
        await runImport(want)
        // Whatever earlier sessions could not store rejoins the real store
        // here. After the import, so those changes land on top of the snapshot
        // they were made against rather than under it.
        await flushAll(engine)
    } catch (err) {
        // Any failure — open, stores, import, or replay — leaves this session
        // on localStorage so the user still sees their data. The next launch
        // retries the new engine, and a replay that only got half way is
        // finished then, since every step of it can be repeated.
        useLocalEngine()
        console.warn('storage: falling back to local', err)
    }
}

// The same net for a failure that only shows up on the first read, after
// openStorage has already returned: boot drops back to localStorage and
// fills the stores again rather than rendering an empty app.
//
// The fallback engine rather than the plain local one, because a session that
// got here is a session whose edits have nowhere else to go — see
// services/storage/fallback.ts.
export const useLocalEngine = () => {
    engine = fallback
    fell = true
    state.set(DEGRADED, true)
}

// Whether this session is one of those. The events service asks before it
// migrates: a fallback session reads the legacy rows off a snapshot that must
// not be migrated for real. See events.init.
export const fellBack = () => fell

// Another go at whatever is still owed. Called when the app comes back to the
// front: storage that was full or busy a moment ago may take the rows now.
export async function retryWrites() {
    if (fell || !pendingCollections().length) return
    try {
        await flushAll(engine)
        state.set(DEGRADED, false)
    } catch (err) {
        console.warn('storage: still cannot write', err)
    }
}

export const collection = <T extends { id: string }>(name: string): ICollection<T> => {
    let writes = Promise.resolve()
    // The catch settles the tail, so the next mutation still runs and nothing
    // is left as an unhandled rejection. What it must not do is let the change
    // itself go: the services have already moved their memory on and told the
    // pages, so a dropped write is an item the user can see and will not find
    // again. It is kept instead, and `keep` is what puts it by.
    const enqueue = (op: () => Promise<void>, keep: () => void) => {
        writes = writes.then(op).catch(err => {
            console.warn(`storage: ${name} write failed`, err)
            keep()
            state.set(DEGRADED, true)
        })
        return writes
    }
    // Anything already owed on this collection goes in first, so a failure
    // heals on the user's next save rather than waiting for a relaunch. Not
    // while this session has fallen back: there the delta is the store, and
    // flushing it into itself would hand back its own rows and then drop them.
    const settled = async (write: () => Promise<void>) => {
        if (!fell) await flushPending(name, engine)
        await write()
        if (!fell && !pendingCollections().length) state.set(DEGRADED, false)
    }
    return {
        load: () => engine<T>(name).load(),
        put: row => enqueue(
            () => settled(() => engine<T>(name).put(row)),
            () => rememberPut(name, row)),
        remove: id => enqueue(
            () => settled(() => engine<T>(name).remove(id)),
            () => rememberRemove(name, id)),
    }
}
